import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { CircularProgress, Box } from '@mui/material'

import ProtectedRoute from '@components/common/ProtectedRoute'
import DashboardLayout from '@components/layout/DashboardLayout'
import Login from '@/pages/auth/Login'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { useAppSelector } from '@/store/hook'
import { USER_ROLES } from '@/components/layout/navigation'


// Lazy load pages
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'))
const Tickets = lazy(() => import('@/pages/tickets/Tickets'))
const TicketDetails = lazy(() => import('@/pages/tickets/TicketDetails'))
const CreateTicket = lazy(() => import('@/pages/tickets/CreateTicket'))
const EditTicket = lazy(() => import('@/pages/tickets/EditTicket'))
const Admins = lazy(() => import('@/pages/admin/Admins'))
const AddAdmin = lazy(() => import('@/pages/admin/AddAdmin'))
const EditAdmin = lazy(() => import('@/pages/admin/EditAdmin'))
const Checkpoints = lazy(() => import('@/pages/checkpoints/Checkpoints'))
const CheckpointForm = lazy(() => import('@/pages/checkpoints/CheckpointForm'))
const ReportsCarInCarOut = lazy(() => import('@/pages/reports/ReportsCarInCarOut'))
const ReportLostTicket = lazy(() => import('@/pages/reports/ReportLostTicket'))
const NotFound = lazy(() => import('@/pages/NotFound'))
const Unauthorized = lazy(() => import('@/pages/Unauthorized'))

const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </Box>
)

function AppRouter() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route
          path="/login"
          element={!isAuthenticated ? <Login /> : <Navigate to="/" replace />}
        />

        {/* Main protected layout */}
        <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
          {/* Dashboard - accessible to all authenticated users */}
          <Route path="/" element={<Dashboard />} />

          {/* Tickets routes - accessible to all authenticated users */}
          <Route path="/tickets">
            <Route index element={<Tickets />} />
            <Route path="new" element={<CreateTicket />} />
            <Route path=":id" element={<TicketDetails />} />
            <Route path=":id/edit" element={<EditTicket />} />
          </Route>

          {/* Admin routes - using new role-based approach */}
          <Route path="/users">
            <Route
              index
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <Admins />
                </ProtectedRoute>
              }
            />
            <Route
              path="new"
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <AddAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path=":id/edit"
              element={
                <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                  <EditAdmin />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Branches - accessible to all authenticated users */}
          <Route path="/checkpoints">
            <Route index element={<Checkpoints />} />
            <Route path="/checkpoints/new" element={<CheckpointForm />} />
            <Route path="/checkpoints/:id/edit" element={<CheckpointForm />} />
          </Route>

          {/* Reports - using legacy requireSuperAdmin approach */}
          <Route path="/reports">
            <Route
              path="car-in-car-out"
              element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <ReportsCarInCarOut />
                </ProtectedRoute>
              }
            />
             <Route
              path="lostTicket"
              element={
                <ProtectedRoute requireSuperAdmin={true}>
                  <ReportLostTicket />
                </ProtectedRoute>
              }
            />
          </Route>
         

          {/* Alternative: Reports using new role-based approach */}
          {/* 
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute allowedRoles={[USER_ROLES.ADMIN]}>
                <Reports />
              </ProtectedRoute>
            } 
          />
          */}
        </Route>

        {/* Unauthorized page */}
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}

export default AppRouter
